/** Detalhe do cartão: a fatura mês a mês, o que entrou nela, o limite e o que se repete.
 *
 * A tela Contas mostra do cartão só a fatura que vence agora, e foi essa a reclamação do
 * dono: não havia onde ver as faturas passadas, as compras, os pagamentos nem as parcelas
 * correndo. Aqui a FATURA é a unidade de leitura — escolhe-se o mês na tira e tudo abaixo
 * responde àquele mês.
 *
 * Nenhum valor de dinheiro é recalculado: o valor da fatura, o saldo devedor do plano e o
 * valor de cada compra vêm do motor. O que esta tela faz é agrupar e somar itens de uma
 * lista para exibir subtotais (o total por categoria, o limite usado).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ChipIcon, Icon } from "../components/Icon";
import {
  AccountTag,
  Empty,
  ErrorBanner,
  Face,
  Gauge,
  Key,
  Label,
  Skeleton,
  Stamp,
} from "../components/ui";
import {
  column,
  dayMonth,
  dayOnly,
  hojeLocal,
  money,
  monthLabel,
  stampLabel,
  sumMoney,
  weekday,
} from "../lib/format";
import * as api from "../lib/api";
import { keys, useAccounts, usePayStatement, usePlans, useRecurrences } from "../lib/queries";
import type { Statement, ZoneColor } from "../lib/types";
import { PagarFatura } from "./Contas";
import { regraEmPalavras } from "./Planejado";

/** O selo e a zona de uma fatura. A face pintada é a primeira leitura: paga é verde,
 * vencida é vermelha, fechada pede atenção, aberta ainda não cobra nada. */
function selo(f: Statement, hoje: string): { text: string; cls: string; zona?: ZoneColor } {
  if (f.status === "paid") return { text: "Paga", cls: "paid", zona: "emerald" };
  if (f.due_on < hoje) return { text: "Atrasada", cls: "late", zona: "rose" };
  if (f.status === "closed") return { text: "Fechada", cls: "pend", zona: "amber" };
  return { text: "Aberta", cls: "plan" };
}

/** "Set" — e "Set/25" quando a fatura é de outro ano, senão dois setembros ficam iguais. */
const mesCurto = (period: string, hoje: string) =>
  monthLabel(`${period}-01`).slice(0, 3) +
  (period.slice(0, 4) === hoje.slice(0, 4) ? "" : `/${period.slice(2, 4)}`);

export function Cartao() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const hoje = hojeLocal();

  const contas = useAccounts();
  const recorrencias = useRecurrences();
  const planos = usePlans();
  const pagarFatura = usePayStatement();
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [pagando, setPagando] = useState<Statement | null>(null);

  // Mesma raiz de chave das faturas: pagar uma fatura invalida ["statements"] e esta lista
  // vem junto, sem precisar de um hook novo em queries.ts.
  const faturas = useQuery({
    queryKey: [...keys.statements, "conta", id],
    queryFn: () => api.getAccountStatements(id),
    enabled: !!id,
  });

  // O servidor devolve por vencimento decrescente; a tira lê-se da mais antiga à mais nova.
  const linha = useMemo(() => [...(faturas.data ?? [])].reverse(), [faturas.data]);

  // A fatura que interessa por padrão é a primeira não paga (a vencida vem antes de tudo);
  // se todas foram pagas, a última.
  const fatura =
    linha.find((f) => f.id === escolhida) ??
    linha.find((f) => f.status !== "paid") ??
    linha[linha.length - 1];

  // Quem sabe o que está numa fatura é a própria fatura: `GET /statements/{id}` já devolve
  // os lançamentos com `card_statement_id` igual ao dela. Filtrar por data não serve —
  // uma parcela de cartão guarda a data DA PARCELA em `settles_on`, não o vencimento da
  // fatura, e a busca por janela de um dia voltava vazia numa fatura com valor.
  const detalhe = useQuery({
    queryKey: keys.statement(fatura?.id ?? ""),
    queryFn: () => api.getStatement(fatura!.id),
    enabled: !!fatura?.id,
  });
  const compras = detalhe;

  // O pagamento da fatura é uma transferência que aponta para ela: não é compra.
  const naFatura = useMemo(
    () =>
      (detalhe.data?.transactions ?? [])
        .filter((t) => t.account_id === id && t.kind !== "transfer")
        .sort((a, b) => a.occurred_on.localeCompare(b.occurred_on)),
    [detalhe.data, id],
  );

  // Os pagamentos já feitos, para dar data e valor real a cada fatura paga.
  const liquidados = useQuery({
    queryKey: keys.transactions({ account_id: id, status: "cleared", limit: 300 }),
    queryFn: () => api.getTransactions({ account_id: id, status: "cleared", limit: 300 }),
    enabled: !!id,
  });
  const pagamentos = useMemo(
    () =>
      new Map(
        (liquidados.data ?? [])
          .filter((t) => t.source === "statement_payment")
          .map((t) => [t.id, t]),
      ),
    [liquidados.data],
  );

  const faixas = useMemo(() => {
    const porCategoria = new Map<string, number[]>();
    for (const t of naFatura) {
      if (t.kind !== "expense") continue;
      const nome = t.category_name ?? "Sem categoria";
      const lista = porCategoria.get(nome);
      if (lista) lista.push(t.amount);
      else porCategoria.set(nome, [t.amount]);
    }
    return [...porCategoria]
      .map(([nome, valores]) => ({ nome, valor: sumMoney(valores) }))
      .sort((a, b) => b.valor - a.valor);
  }, [naFatura]);

  // A tira rola no dedo; trocar de fatura pelas setas tem de trazer o chip escolhido à vista.
  const chipRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    chipRef.current?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [fatura?.id]);

  if (contas.isPending || faturas.isPending)
    return (
      <section className="view on">
        <Skeleton lines={6} />
      </section>
    );

  const falha = (contas.error ?? faturas.error) as Error | null;
  if (falha)
    return (
      <section className="view on">
        <ErrorBanner
          message={falha.message}
          onRetry={() => {
            contas.refetch();
            faturas.refetch();
          }}
        />
      </section>
    );

  const cartao = (contas.data ?? []).find((a) => a.id === id);
  if (!cartao || cartao.kind !== "credit_card")
    return (
      <section className="view on">
        <Empty title="Cartão não encontrado">
          Talvez tenha sido arquivado.{" "}
          <button type="button" className="causelink" onClick={() => nav("/contas")}>
            voltar para contas
          </button>
        </Empty>
      </section>
    );

  const liquidacao = (contas.data ?? []).find((a) => a.id === cartao.settlement_account_id);
  const gastaveis = (contas.data ?? []).filter((a) => a.in_cashflow);
  const limite = cartao.credit_limit ?? 0;
  const usado = sumMoney(linha.filter((f) => f.status !== "paid").map((f) => f.amount_current));
  const pagas = (faturas.data ?? []).filter((f) => f.status === "paid");
  const parcelas = (planos.data ?? []).filter((p) => p.account_id === id);
  const assinaturas = (recorrencias.data ?? []).filter((r) => r.account_id === id && r.active);
  const total = sumMoney(faixas.map((f) => f.valor));
  const atual = fatura ? selo(fatura, hoje) : null;
  const posicao = fatura ? linha.findIndex((f) => f.id === fatura.id) : -1;

  return (
    <section className="view on" aria-label={`Cartão ${cartao.name}`}>
      <div className="cartao">
        <div className="monthbar">
          <Key icon="chevronLeft" title="Voltar para contas" onClick={() => nav("/contas")} />
          <h2>{cartao.name}</h2>
          <span className="rule">
            fecha dia {cartao.closing_day} · vence dia {cartao.due_day}
          </span>
          {/* Editar cartão não tinha caminho nenhum: o editor completo (fechamento,
              vencimento, limite, cor, conta que paga) vive na tela da conta, e o cartão
              é uma conta. Sem este botão, errar o dia do fechamento era definitivo. */}
          <Key icon="pen" title="Editar cartão" onClick={() => nav(`/contas/conta/${cartao.id}`)}>
            Editar
          </Key>
        </div>

        <div className="ccard">
          <div
            className="pvc"
            style={
              {
                "--c1": cartao.color ?? "#2A2E31",
                "--c2": "#17191B",
              } as React.CSSProperties
            }
          >
            <div className="top">
              <span className="bank">{cartao.name}</span>
              {/* a classe importa: `.pvc .wave` é quem dá 20px ao contactless */}
              <Icon name="wave" className="wave" />
            </div>
            <div className="mid">
              <ChipIcon />
              <span className="number">•••• •••• •••• ••••</span>
            </div>
            <div className="bot">
              <span className="holder">{cartao.owner_name ?? ""}</span>
              <span className="meta">
                fecha dia {cartao.closing_day}
                <br />
                vence dia {cartao.due_day}
              </span>
            </div>
          </div>
          <Face className="cplate">
            <div className="r">
              <Label>Limite usado</Label>
              <Label dim>faturas não pagas</Label>
            </div>
            <div className="r">
              <span className="v">{money(usado)}</span>
              <span className="rule num">
                {limite > 0 ? `${money(Math.max(0, limite - usado))} disponível` : "sem limite cadastrado"}
              </span>
            </div>
            {limite > 0 && (
              <>
                <Gauge pct={(usado / limite) * 100} />
                <span className="rule">de {money(limite)} de limite</span>
              </>
            )}
            {liquidacao && (
              <div className="rule">
                paga por <AccountTag name={liquidacao.name} color={liquidacao.color} />
              </div>
            )}
          </Face>
        </div>

        <div>
          <div className="lblrow">
            <Label>Faturas</Label>
            <span style={{ display: "flex", gap: 6 }}>
              <Key
                icon="chevronLeft"
                title="Fatura anterior"
                disabled={posicao <= 0}
                onClick={() => setEscolhida(linha[posicao - 1].id)}
              />
              <Key
                icon="chevronRight"
                title="Fatura seguinte"
                disabled={posicao < 0 || posicao >= linha.length - 1}
                onClick={() => setEscolhida(linha[posicao + 1].id)}
              />
            </span>
          </div>
          {linha.length === 0 ? (
            <Empty title="Sem fatura ainda">
              A primeira fatura nasce na primeira compra lançada neste cartão.
            </Empty>
          ) : (
            <div className="months" role="group" aria-label="Mês da fatura">
              {linha.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className="chip"
                  aria-pressed={f.id === fatura?.id}
                  ref={f.id === fatura?.id ? chipRef : undefined}
                  title={`Fatura de ${monthLabel(`${f.period}-01`)} · vence ${dayMonth(f.due_on)}`}
                  onClick={() => setEscolhida(f.id)}
                >
                  <b>{mesCurto(f.period, hoje)}</b>
                  <small>{selo(f, hoje).text}</small>
                </button>
              ))}
            </div>
          )}
        </div>

        {fatura && atual && (
          <Face className="meter" zone={atual.zona}>
            <div className="lblrow">
              <Label>Fatura de {monthLabel(`${fatura.period}-01`)}</Label>
              <Stamp text={atual.text} cls={atual.cls} />
            </div>
            <div className="reading">
              {money(
                fatura.status === "paid" ? (fatura.amount_closed ?? fatura.amount_current) : fatura.amount_current,
              )}
            </div>
            <div className="sub">
              fecha {dayMonth(fatura.closing_on)} · vence {dayMonth(fatura.due_on)} ·{" "}
              {naFatura.length} {naFatura.length === 1 ? "compra" : "compras"}
            </div>
            {fatura.status !== "paid" && (
              <div className="lblrow">
                <span className="rule">
                  {fatura.due_on < hoje
                    ? `venceu em ${dayMonth(fatura.due_on)}`
                    : `pagar até ${dayMonth(fatura.due_on)}`}
                </span>
                <Key
                  primary
                  disabled={!(fatura.amount_current > 0) || pagarFatura.isPending}
                  onClick={() => setPagando(fatura)}
                >
                  Pagar fatura
                </Key>
              </div>
            )}
          </Face>
        )}

        {faixas.length > 0 && total > 0 && (
          <Face className="brk">
            <div className="lblrow">
              <Label>Onde foi</Label>
              <Label dim>
                {faixas.length} {faixas.length === 1 ? "categoria" : "categorias"}
              </Label>
            </div>
            {faixas.map((f) => (
              <div className="r" key={f.nome}>
                <span>{f.nome}</span>
                <span className="n">{money(f.valor)}</span>
                {/* a barra é a pena de saída: proporção dentro da própria fatura */}
                <div className="gauge">
                  <i style={{ width: `${(f.valor / total) * 100}%`, background: "var(--p-out)" }} />
                </div>
              </div>
            ))}
          </Face>
        )}

        <Face className="log">
          <div className="sec">
            <Label>Compras da fatura</Label>
            <Label dim>{fatura ? monthLabel(`${fatura.period}-01`) : "—"}</Label>
          </div>
          {compras.isPending && <Skeleton lines={3} />}
          {compras.error && (
            <ErrorBanner
              message={(compras.error as Error).message}
              onRetry={() => compras.refetch()}
            />
          )}
          {!compras.isPending && !compras.error && naFatura.length === 0 && (
            <Empty title="Nenhuma compra nesta fatura">
              {fatura
                ? `O que você lançar neste cartão até ${dayMonth(fatura.closing_on)} entra aqui.`
                : "Lance uma compra neste cartão para a fatura nascer."}
            </Empty>
          )}
          {naFatura.map((t) => {
            const st = stampLabel({
              status: t.status,
              estimated: t.estimated,
              kind: t.kind,
              bucket: t.bucket,
            });
            const parcelada = (t.installment_total ?? 0) > 1;
            return (
              <div className={`line p-${column(t)}`} key={t.id}>
                <div className="d">
                  {weekday(t.occurred_on)}
                  <b>{dayOnly(t.occurred_on)}</b>
                </div>
                <div className="what">
                  <div className="t">
                    <span className="desc">{t.description || t.payee || "Sem descrição"}</span>
                    <Stamp text={st.text} cls={st.cls} />
                  </div>
                  <small>
                    {t.category_name ?? "sem categoria"}
                    {t.payee && t.description ? ` · ${t.payee}` : ""}
                  </small>
                </div>
                <div className="amt">
                  <span>
                    {t.kind === "income" ? "+" : "−"}
                    {money(t.amount)}
                  </span>
                  {parcelada && (
                    <small>
                      parcela {t.installment_no} de {t.installment_total}
                    </small>
                  )}
                </div>
              </div>
            );
          })}
          {fatura && naFatura.length > 0 && (
            <div className="line total">
              <span />
              <span>
                {naFatura.length} {naFatura.length === 1 ? "compra" : "compras"} · vence{" "}
                {dayMonth(fatura.due_on)}
              </span>
              <span className="amt">−{money(fatura.amount_current)}</span>
            </div>
          )}
        </Face>

        {parcelas.length > 0 && (
          <Face className="log">
            <div className="sec">
              <Label>Parcelas correndo</Label>
              <Label dim>{parcelas.length} no cartão</Label>
            </div>
            {parcelas.map((p) => (
              <div className="line p-out" key={p.id}>
                <div className="d">
                  {p.next_on ? "próx." : ""}
                  <b>{p.next_on ? dayOnly(p.next_on) : "—"}</b>
                </div>
                <div className="what">
                  <div className="t">
                    <span className="desc">{p.description}</span>
                  </div>
                  <small>
                    {p.paid_count ?? 0} de {p.installment_count} pagas
                    {p.payee ? ` · ${p.payee}` : ""}
                  </small>
                  <Gauge pct={((p.paid_count ?? 0) / p.installment_count) * 100} />
                </div>
                <div className="amt">
                  <span>−{money(p.remaining ?? 0)}</span>
                  <small>restam {p.installment_count - (p.paid_count ?? 0)}×</small>
                </div>
              </div>
            ))}
          </Face>
        )}

        <Face className="log">
          <div className="sec">
            <Label>Pagamentos</Label>
            <Label dim>
              {pagas.length} {pagas.length === 1 ? "fatura paga" : "faturas pagas"}
            </Label>
          </div>
          {pagas.length === 0 ? (
            <Empty title="Nenhuma fatura paga ainda">
              Ao pagar uma fatura, ela fica aqui com a data e o valor que saiu da conta.
            </Empty>
          ) : (
            pagas.map((f) => {
              const tx = f.payment_transaction_id ? pagamentos.get(f.payment_transaction_id) : undefined;
              const quando = tx?.settles_on ?? f.due_on;
              return (
                <div className="line p-sv" key={f.id}>
                  <div className="d">
                    {weekday(quando)}
                    <b>{dayOnly(quando)}</b>
                  </div>
                  <div className="what">
                    <div className="t">
                      <span className="desc">Fatura de {monthLabel(`${f.period}-01`)}</span>
                      <Stamp text="Paga" cls="paid" />
                    </div>
                    <small>
                      {tx ? `pago em ${dayMonth(tx.settles_on)}` : `vencimento ${dayMonth(f.due_on)}`}
                      {tx?.account_name ? ` · ${tx.account_name}` : ""}
                    </small>
                  </div>
                  <div className="amt">
                    <span>−{money(tx?.amount ?? f.amount_closed ?? f.amount_current)}</span>
                  </div>
                </div>
              );
            })
          )}
        </Face>

        <Face className="log">
          <div className="sec">
            <Label>Repete neste cartão</Label>
            <Label dim>{assinaturas.length} ativas</Label>
          </div>
          {recorrencias.isPending && <Skeleton lines={2} />}
          {!recorrencias.isPending && assinaturas.length === 0 && (
            <Empty title="Nada se repete neste cartão">
              Assinaturas e mensalidades cadastradas em Planejado, com este cartão como conta,
              aparecem aqui.
            </Empty>
          )}
          {assinaturas.map((r) => (
            <div
              className={`line p-${r.tx_kind === "income" ? "in" : r.tx_kind === "transfer" ? "sv" : "out"}`}
              key={r.id}
            >
              <div className="d">
                {r.next_on ? "próx." : ""}
                <b>{r.next_on ? dayOnly(r.next_on) : "—"}</b>
              </div>
              <div className="what">
                <div className="t">
                  <span className="desc">{r.name}</span>
                  {r.is_variable && <Stamp text="Estimado" cls="est" />}
                </div>
                <small>
                  {regraEmPalavras(r)}
                  {r.category_name ? ` · ${r.category_name}` : ""}
                </small>
              </div>
              <div className="amt">
                <span>
                  {r.tx_kind === "income" ? "+" : "−"}
                  {money(r.amount)}
                </span>
              </div>
            </div>
          ))}
        </Face>
      </div>

      {pagando && (
        <PagarFatura
          fatura={pagando}
          contas={gastaveis}
          onClose={() => setPagando(null)}
          onPagar={(body) => {
            pagarFatura.mutate({ id: pagando.id, ...body });
            setPagando(null);
          }}
        />
      )}
    </section>
  );
}
